<template>
  <div>
    <loading-overlay v-if="updating" :text="''" />
    
    <!-- Permission Check: Nur für Admins und Manager (KEIN POS) -->
    <v-container fluid class="px-6 py-6" v-if="!hasAdminOrManagerPermission">
      <permission-denied 
        message="Du benötigst Manager- oder Administrator-Rechte, um News zu verwalten."
      />
    </v-container>
    
    <v-container fluid class="px-6 py-6" v-else-if="news">
      <v-row class="mb-5">
        <v-col cols="12">
          <!-- Archived Badge -->
          <v-chip v-if="news.archived" color="grey" dark class="mr-2 mb-2">
            <i class="material-icons-round mr-1" style="font-size: 18px;">inventory</i>
            News ist archiviert
          </v-chip>
          
          <!-- Details Button -->
          <v-btn
            elevation="0"
            height="43"
            class="
              font-weight-bold
              text-uppercase
              btn-default
              bg-gradient-primary
              py-2
              px-6
              me-2
              mb-sm-0 mb-2
            "
            color="#5e72e4"
            small
            @click="$router.push('/admin/news/details/' + news._id).catch(() => {})"
            >News Details</v-btn
          >
          
          <!-- Archive/Unarchive Button -->
          <v-btn
            v-if="canArchiveNews"
            elevation="0"
            height="43"
            class="
              font-weight-bold
              text-uppercase
              py-2
              px-6
              me-2
              mb-sm-0 mb-2
            "
            :color="news.archived ? 'success' : 'warning'"
            small
            @click="news.archived ? showUnarchiveDialog = true : showArchiveDialog = true"
          >
            <i class="material-icons-round mr-1" style="font-size: 18px;">
              {{ news.archived ? 'unarchive' : 'inventory' }}
            </i>
            {{ news.archived ? 'News dearchivieren' : 'News archivieren' }}
          </v-btn>
        </v-col>
      </v-row>
      
      <!-- Archive Confirmation Dialog -->
      <v-dialog v-model="showArchiveDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">News archivieren</v-card-title>
          <v-card-text>
            Möchtest Du diese News wirklich archivieren? Die News wird deaktiviert und ist nicht mehr öffentlich sichtbar.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showArchiveDialog = false">Abbrechen</v-btn>
            <v-btn color="warning" text @click="archiveNews">Archivieren</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      
      <!-- Unarchive Confirmation Dialog -->
      <v-dialog v-model="showUnarchiveDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">News dearchivieren</v-card-title>
          <v-card-text>
            Möchtest Du diese News dearchivieren? Die News kann dann wieder bearbeitet und veröffentlicht werden.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="showUnarchiveDialog = false">Abbrechen</v-btn>
            <v-btn color="success" text @click="unarchiveNews">Dearchivieren</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-row class="m-0">
        <v-col cols="12" class="pt-0 mb-10 mt-5">
          <v-card class="mb-30 card card-shadow border-radius-xl py-5">
            <div class="mt-n11 mx-4 card-header position-relative z-index-2">
              <div class="d-block blur-shadow-image w-100">
                <img
                  v-if="!imageError && news.img"
                  :src="getImage(news.img)"
                  class="img-fluid shadow border-radius-lg"
                  :alt="news.img"
                  @error="imageError = true"
                  style="height: 400px; object-fit: cover; width: 100%;"
                />
                <div
                  v-else
                  class="d-flex align-center justify-center shadow border-radius-lg"
                  style="
                    height: 400px;
                    width: 100%;
                    background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
                  "
                >
                  <i class="material-icons-round" style="font-size: 100px; color: white; opacity: 0.5;">
                    image_not_supported
                  </i>
                </div>
              </div>
              <div
                v-if="!imageError && news.img"
                class="colored-shadow"
                v-bind:style="{
                  backgroundImage: 'url(' + getImage(news.img) + ')',
                }"
              ></div>
            </div>
            <div class="card-header-padding">
              <v-row>
                <v-col>
                  <p class="font-weight-600 text-h3 text-typo mb-0">
                    {{ news.title }}
                  </p>
                </v-col>
              </v-row>
            </div>
          </v-card>
          <v-card class="card-shadow border-radius-xl">
            <v-card-text class="card-padding">
              <h5 class="font-weight-bolder text-h5 text-typo mb-0">
                News Informationen
              </h5>
              <v-row>
                <v-col cols="12" lg="4" class="mt-5">
                  <h6
                    class="
                      text-caption text-uppercase
                      font-weight-600
                      text-muted
                    "
                  >
                    Neues Titelbild hochladen (optional)
                  </h6>
                  <dropzone @newFile="setFile"></dropzone>
                </v-col>
              </v-row>
              <div class="border-bottom my-5"></div>
              <v-form>
                <div class="ps-lg-5 mt-5">
                  <v-row>
                    <v-col>
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 d-block"
                        >Newstitel</label
                      >
                      <v-text-field
                        :rules="rules"
                        required
                        outlined
                        v-model="news.title"
                        class="
                          input-style
                          font-size-input
                          text-light-input
                          placeholder-light
                        "
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 d-block"
                        >Urheber- und Fotoinformationen</label
                      >
                      <v-text-field
                        outlined
                        v-model="news.disclaimer"
                        class="
                          input-style
                          font-size-input
                          text-light-input
                          placeholder-light
                        "
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <label
                        for=""
                        class="label-color font-weight-600 mb-2 d-block"
                        >Newscontent</label
                      >
                      <vue-editor v-model="news.description"></vue-editor>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>
          </v-card>

          <v-card class="card-shadow border-radius-xl mt-5">
            <v-card-text class="card-padding">
              <v-row class="">
                <v-col>
                  <v-row align="center" justify="space-around">
                    <v-btn
                      elevation="0"
                      :ripple="false"
                      height="43"
                      :disabled="checkForm || news.archived"
                      @click="postNewsTest()"
                      class="
                        font-weight-600
                        text-capitalize
                        btn-neutral
                        py-3
                        px-6
                        rounded-sm
                      "
                      color="#fff"
                      >News speichern</v-btn
                    >
                    <v-btn
                      elevation="0"
                      :ripple="false"
                      height="43"
                      @click="resetUserData()"
                      class="
                        font-weight-600
                        text-capitalize
                        btn-neutral
                        py-3
                        px-6
                        rounded-sm
                      "
                      color="#fff"
                      >Änderungen zurücksetzen</v-btn
                    >
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-snackbar
        top
        v-model="snackbar.visible"
        :color="snackbar.color"
        class="snackbar-shadow"
      >
        <div class="d-flex align-start alert-notify">
          <v-icon size="24" class="text-white me-3 mt-1 material-icons-round"
            >notifications</v-icon
          >
          <p class="mb-0">
            <span class="font-size-root font-weight-600">{{
              snackbar.title
            }}</span>
            <br />
            {{ snackbar.message }}
          </p>
        </div>

        <template v-slot:action="{ attrs }">
          <v-btn
            icon
            elevation="0"
            max-width="136"
            :ripple="false"
            height="43"
            class="font-weight-600 text-capitalize py-3 px-6 rounded-sm"
            color="rgba(255,255,255, .85)"
            @click="snackbar.visible = false"
            v-bind="attrs"
          >
            <v-icon size="13">fas fa-times</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>
<script>
// import CalendarWidget from "./Widgets/CalendarWidget.vue";
import { VueEditor } from "vue2-editor";
import Dropzone from "./Widgets/Dropzone.vue";
import coreData from "@/mixins/coreDataMixin";
import permissionMixin from "@/mixins/permissionMixin";
import PermissionDenied from "@/components/PermissionDenied.vue";
import axiosAuth from "@/shared/configs/axios-auth";

export default {
  name: "NewsEdit",
  mixins: [coreData, permissionMixin],
  components: {
    VueEditor,
    Dropzone,
    PermissionDenied,
  },
  data: function () {
    return {
      imageError: false,
      showArchiveDialog: false,
      showUnarchiveDialog: false,
    };
  },
  async mounted() {
    // Direkter Aufruf: News per ID laden
    const newsId = this.$route.params.id;
    if (newsId) {
      try {
        // IMMER vom Backend laden, um vollständige Daten zu haben
        console.log('🔄 [NEWS EDIT] Loading news by ID:', newsId);
        await this.$store.dispatch("getEventNewsByID", newsId);
        console.log('✅ [NEWS EDIT] News loaded from backend');
      } catch (error) {
        console.error('❌ [NEWS EDIT] Fehler beim Laden der News:', error);
      }
    }
  },
  watch: {
    news: {
      handler(newNews) {
        if (newNews && newNews._id) {
          console.log("news is there", newNews._id);
          this.imageError = false;
        } else if (newNews) {
          console.log("⚠️ [NEWS EDIT] News loaded but incomplete");
        }
      },
      immediate: true,
    },
    'news.img'() {
      this.imageError = false;
    },
    '$route.params.id': {
      async handler(newId, oldId) {
        if (newId && newId !== oldId) {
          console.log(`[NEWS EDIT] Route changed from ${oldId} to ${newId}`);
          // News neu laden
          await this.$store.dispatch("getEventNewsByID", newId);
        }
      }
    },
  },
  computed: {
    checkForm() {
      if (this.news.title && this.news.description) return false;
      return true;
    },
    canArchiveNews() {
      // Only the actual owner (admin: true) can archive/unarchive
      if (!this.news || !this.userInformations) return false;
      return (
        this.userInformations.admin &&
        this.news.manager === this.userInformations._id
      );
    },
  },
  methods: {
    async archiveNews() {
      try {
        const response = await this.$http.patch(
          `/eventnews/archive/${this.news._id}`
        );
        this.showArchiveDialog = false;
        // Update local news object
        this.news.archived = true;
        this.news.archivedAt = response.data.archivedAt;
        this.news.active = false;
        this.SnackbarShow("Success", "Aktion erfolgreich!", "News archiviert!");
      } catch (error) {
        console.error("Error archiving news:", error);
        this.SnackbarShow("Danger", "Fehler!", "News konnte nicht archiviert werden.");
      }
    },
    async unarchiveNews() {
      try {
        await this.$http.patch(`/eventnews/unarchive/${this.news._id}`);
        this.showUnarchiveDialog = false;
        // Update local news object
        this.news.archived = false;
        this.news.archivedAt = null;
        this.SnackbarShow("Success", "Aktion erfolgreich!", "News dearchiviert!");
      } catch (error) {
        console.error("Error unarchiving news:", error);
        this.SnackbarShow("Danger", "Fehler!", "News konnte nicht dearchiviert werden.");
      }
    },
    setFile(file) {
      console.log("file emitted!");
      this.news.file = file;
      this.imageError = false;
    },
    updateNews() {
      this.$store.dispatch("updateEventNews", this.news);
      this.SnackbarShow("Success");
    },
    postNewsTest() {
      if (!this.news.title || !this.news.description) return;
      this.$store.commit("updating", true);
      return new Promise((resolve, reject) => {
        let formData = new FormData();
        formData.append("description", this.news.description);
        formData.append("title", this.news.title);
        formData.append("disclaimer", this.news.disclaimer);
        // Manager and client_id are not sent - they must not be changed during edit
        // Backend will ignore these fields anyway (delete req.body.manager)
        if (this.news.file) {
          formData.append("image", this.news.file);
        } else {
          formData.append("img", this.news.img);
        }
        formData.append("date", this.news.date);

        axiosAuth
          .patch("eventnews/" + this.news._id, formData)
          .then(() => {
            resolve(true);
            this.uploadComplete = true;
            this.$store.commit("updating", false);
            this.$store.dispatch("getEventNewsAdmin");
            this.SnackbarShow(
              "Success",
              "Aktion erfolgreich!",
              "News gespeichert!"
            );
          })
          .catch((error) => {
            this.$store.commit("updating", false);
            console.log(error.response);
            this.SnackbarShow(
              "Danger",
              "Fehler!",
              "Etwas ist schief gelaufen..."
            );
            reject(error.response.data);
          });
      });
    },
  },
};
</script>
